import { useState } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import {
  Box,
  Center,
  Collapse,
  Drawer,
  Flex,
  Group,
  rem,
  ScrollArea,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { createStyles, getStylesRef } from '@mantine/emotion';
import { useAuth } from '@/contexts/AuthContext';
import { linksMenu, linksMenuHomepage, visibleSubmenus } from './linksMenu';
import NavbarActions from './NavbarActions';

type DrawerCollapseProps = {
  drawerOpened: boolean;
  closeDrawer: () => void;
  isHomepage?: boolean;
};

export default function DrawerCollapse({
  drawerOpened,
  closeDrawer,
  isHomepage,
}: DrawerCollapseProps) {
  const { classes, theme } = useStyles();
  const { currentUser } = useAuth();

  const [linksOpened, setLinksOpened] = useState<number | null>(null);

  const toggleLinks = (id: number) => {
    setLinksOpened((prevId) => (prevId === id ? null : id));
  };

  return (
    <Drawer
      opened={drawerOpened}
      onClose={closeDrawer}
      size="100%"
      padding="md"
      title="Navegação"
      className={classes.hiddenDesktop}
      zIndex={1000000}
    >
      <ScrollArea h={`calc(100vh - ${rem(0)})`} mx="-md">
        {isHomepage &&
          linksMenuHomepage.map((link) => (
            <ScrollLink
              key={link.id}
              to={link.href}
              smooth
              duration={500}
              offset={-50}
              href={link.href}
              className={classes.link}
              onClick={closeDrawer}
            >
              {link.label}
            </ScrollLink>
          ))}

        {!isHomepage &&
          linksMenu.map((link) => (
            <div key={link.id}>
              <UnstyledButton className={classes.link} onClick={() => toggleLinks(link.id)}>
                <Center inline>
                  <Box component="span" mr={5} tt="uppercase">
                    {link.label}
                  </Box>

                  <IconChevronDown size={16} className={classes.linkIcon} />
                </Center>
              </UnstyledButton>

              <Collapse in={linksOpened === link.id}>
                {link.submenu &&
                  visibleSubmenus(link.submenu, currentUser?.role).map((submenu) => (
                    <UnstyledButton className={classes.subLink} key={submenu.label}>
                      <Link
                        to={submenu.href}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                        }}
                        onClick={closeDrawer}
                      >
                        <Group align="center">
                          <ThemeIcon size={34} variant="default" radius="md">
                            {submenu.icon && (
                              <submenu.icon
                                size={22}
                                stroke={1.5}
                                color={theme.colors.principal[5]}
                              />
                            )}
                          </ThemeIcon>

                          {submenu.label}
                        </Group>
                      </Link>
                    </UnstyledButton>
                  ))}
              </Collapse>
            </div>
          ))}

        {/* Mesmos botões do menu do desktop (NavbarActions) */}
        <Flex p={20} direction="column">
          <NavbarActions fullWidth />
        </Flex>
      </ScrollArea>
    </Drawer>
  );
}

const useStyles = createStyles((theme, _, u) => ({
  link: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: theme.fontSizes.sm,
    [u.light]: {
      color: theme.black,
    },

    [u.dark]: {
      color: theme.white,
      backgroundColor: theme.colors.dark[6],
    },

    [u.smallerThan('md')]: {
      height: rem(42),
      display: 'flex',
      alignItems: 'center',
      width: '100%',
    },

    '&:hover': {
      fontWeight: 600,
      backgroundColor: theme.colors.principal[5],

      [u.light]: {
        color: theme.colors.gray[0],
      },

      [u.dark]: {
        color: theme.colors.dark[6],
      },

      [`& .${getStylesRef('icon')}`]: {
        [u.light]: {
          color: theme.white,
        },

        [u.dark]: {
          color: theme.black,
        },
      },
    },
  },
  subLink: {
    width: '100%',
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    borderRadius: theme.radius.md,

    '&:hover': {
      backgroundColor: theme.colors.principal[5],
      [u.light]: {
        color: theme.white,
      },

      [u.dark]: {
        color: theme.colors.dark[6],
      },

      [`& .${getStylesRef('icon')}`]: {
        backgroundColor: theme.colors.principal[5],
      },
    },

    '&:active': {
      backgroundColor: theme.colors.principal[5],
      [u.light]: {
        color: theme.white,
      },

      [u.dark]: {
        color: theme.colors.dark[6],
      },

      [`& .${getStylesRef('icon')}`]: {
        backgroundColor: theme.colors.principal[5],
      },
    },
  },
  linkIcon: {
    ref: getStylesRef('icon'),
    [u.light]: {
      color: theme.colors.secondary,
    },

    [u.dark]: {
      color: theme.colors.dark[2],
    },
  },

  hiddenDesktop: {
    [u.largerThan('md')]: {
      display: 'none',
    },
  },
}));
